import { useCallback, useEffect, useRef, useState } from "react";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import utilityFunctions from "@store/utilityFunctions";
import config from "@config";
import { cleanParams } from "./useApiResource";
import { chipParams, DEFAULT_CHIP } from "./config/sla";

const SEARCH_DEBOUNCE_MS = 400;

/*
Brand scoping, mirroring getBrandIdsForRetrieval in pages/Warranties/Warranties.js.
"all" expands to the comma-joined ids of the user's brands in this country;
otherwise the single selected brand id is sent.
*/
export const brandParams = function (selectedbrandID, selectedbrandAll) {
  if (selectedbrandAll === "all") {
    const userBrands = utilityFunctions.getUserInfo()?.user?.brands || [];
    return {
      brandIds: userBrands
        .filter(function (b) {
          return b.country === config.country;
        })
        .map(function (b) {
          return b.id;
        })
        .join(","),
    };
  }
  return { brandId: selectedbrandID };
};

/*
Owns the case list query: chip, search, ordering and paging, plus the fetch.

Kept out of useApiResource because the search term needs debouncing and any
filter change has to reset the page - otherwise a search made on page 4 returns
an empty page and reads as a broken table.

`cardFilters` is the `filters` dict of a clicked dashboard card (RMAS-129).
While set it replaces the chip's params entirely - the chips' default
`filter_criteria=all` hides closed tickets and would empty a "Completed" card -
and is merged over the scope params, so a key it shares with them (a team
velocity row's `service_center_central`) wins.
*/
export default function useCaseList(
  protectedFetch,
  selectedbrandID,
  selectedbrandAll,
  cardFilters
) {
  const [query, setQuery] = useState({
    chip: DEFAULT_CHIP,
    q: "",
    ordering: "-last_activity_date",
    page: 1,
    pageSize: 25,
  });

  // Serialised for a stable dep - the dict is re-read from the dashboard
  // response on every render.
  const cardFiltersKey = cardFilters ? JSON.stringify(cardFilters) : "";

  // A new card filter starts back on page 1. Adjusted during render, not in an
  // effect, so the list isn't fetched once with the stale page first.
  const [prevCardFiltersKey, setPrevCardFiltersKey] = useState(cardFiltersKey);
  if (prevCardFiltersKey !== cardFiltersKey) {
    setPrevCardFiltersKey(cardFiltersKey);
    if (query.page !== 1) {
      setQuery({ ...query, page: 1 });
    }
  }
  // The input is controlled immediately; only `query.q` is debounced.
  const [searchInput, setSearchInput] = useState("");
  const [result, setResult] = useState({
    loading: true,
    error: null,
    rows: [],
    totalRows: 0,
    hasLoadedOnce: false,
  });

  useEffect(
    function () {
      const timer = setTimeout(function () {
        setQuery(function (prev) {
          if (prev.q === searchInput) return prev;
          return { ...prev, q: searchInput, page: 1 };
        });
      }, SEARCH_DEBOUNCE_MS);
      return function () {
        clearTimeout(timer);
      };
    },
    [searchInput]
  );

  const ignoreRef = useRef(false);

  const load = useCallback(
    function () {
      if (!protectedFetch) return;

      setResult(function (prev) {
        return { ...prev, loading: true, error: null };
      });

      const scope = brandParams(selectedbrandID, selectedbrandAll);
      const filterParams = cardFiltersKey
        ? { ...scope, ...JSON.parse(cardFiltersKey) }
        : { ...chipParams(query.chip), ...scope };

      const params = cleanParams({
        ...filterParams,
        q: query.q,
        ordering: query.ordering,
        page: query.page,
        page_size: query.pageSize,
      });

      protectedFetch(
        apiEndpoints.tickets.list(),
        "GET",
        params,
        errorFunctions.checkResponseThenJson
      )
        .then(function (res) {
          if (ignoreRef.current) return;
          setResult({
            loading: false,
            error: null,
            rows: res?.results || [],
            totalRows: res?.count || 0,
            hasLoadedOnce: true,
          });
        })
        .catch(function (err) {
          if (ignoreRef.current) return;
          setResult({
            loading: false,
            error: (err && err.message) || "Failed to load cases.",
            rows: [],
            totalRows: 0,
            hasLoadedOnce: true,
          });
        });
    },
    [
      protectedFetch,
      selectedbrandID,
      selectedbrandAll,
      cardFiltersKey,
      query.chip,
      query.q,
      query.ordering,
      query.page,
      query.pageSize,
    ]
  );

  useEffect(
    function () {
      ignoreRef.current = false;
      load();
      return function () {
        ignoreRef.current = true;
      };
    },
    [load]
  );

  // Every filter-ish change resets to page 1; paging itself does not.
  const setChip = function (chip) {
    setQuery(function (prev) {
      return { ...prev, chip, page: 1 };
    });
  };
  const setOrdering = function (ordering) {
    setQuery(function (prev) {
      return { ...prev, ordering, page: 1 };
    });
  };
  const setPage = function (page) {
    setQuery(function (prev) {
      return { ...prev, page };
    });
  };
  const setPageSize = function (pageSize) {
    setQuery(function (prev) {
      return { ...prev, pageSize, page: 1 };
    });
  };
  const clearFilters = function () {
    setSearchInput("");
    setQuery(function (prev) {
      return { ...prev, chip: DEFAULT_CHIP, q: "", page: 1 };
    });
  };

  return {
    query,
    searchInput,
    setSearchInput,
    setChip,
    setOrdering,
    setPage,
    setPageSize,
    clearFilters,
    reload: load,
    ...result,
  };
}
